import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/lib/api-client';

/**
 * SubscriptionState — wire-shape of `GET /api/subscriptions/me`. Mirrors
 * `src/subscriptions/dto/subscription-state-response.dto.ts` (cross-repo
 * shape — keep in sync).
 *
 * Date fields arrive as ISO-8601 strings on the wire. The backend always
 * returns 200 (never 404) — when the user has no active subscription
 * (trial-only), the DTO carries `status: 'trial'` and all subscription-
 * specific fields are null/zero/false. PWA code never has to special-
 * case missing-subscription HTTP semantics.
 */
export interface SubscriptionState {
  status: 'active' | 'trial' | 'past_due' | 'cancelled';
  tierKey: string | null;
  tierLabel: string | null;
  billingInterval: 'monthly' | 'annual' | null;
  amountCents: number | null;
  currency: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  /**
   * Stripe billing-cycle end (item.current_period_end verbatim). For
   * monthly subs this equals currentPeriodEnd; for annual subs it's
   * start + 1 year and drives the A-02 "Nächste Abrechnung" line.
   * Distinct from currentPeriodEnd which is the credit-refresh window
   * end (always +1 month per Backend #39 doctrine).
   */
  stripeBillingCycleEnd: string | null;
  storiesPerMonth: number;
  storiesUsedThisMonth: number;
  cancelAtPeriodEnd: boolean;
  endsAt: string | null;
  pendingDowngradeTier: string | null;
  pendingDowngradeTierLabel: string | null;
  pendingDowngradeAt: string | null;
  /**
   * Bug #5 (2026-05-13): pending billing-interval at the scheduled
   * transition. Surfaces cycle-switch (Monthly↔Yearly same tier) which
   * is invisible from `pendingDowngradeTier` alone (it would equal the
   * current tier for a cycle-switch). PWA components that render
   * "Plan-Wechsel zum X" copy may use this field to differentiate
   * cycle-switch from true downgrade — currently additive, no required
   * component change pre-launch.
   */
  pendingBillingInterval: 'monthly' | 'annual' | null;
  isPastDue: boolean;
  subscriptionId: string | null;
  stripeSubscriptionId: string | null;
  rawSubscriptionStatus: 'active' | 'past_due' | 'cancelled' | 'expired' | null;
  /**
   * §356a (L.1): true when this subscription is within its online-withdrawal
   * window. Backend forces false when FEATURE_WITHDRAWAL_BUTTON_ENABLED is off,
   * so the "Vertrag widerrufen" button stays hidden without a frontend flag.
   */
  canWithdraw: boolean;
  /** §356a (L.1): end of the 14-day withdrawal window, ISO-8601, or null. */
  withdrawalDeadline: string | null;
}

/**
 * Lifecycle-phase discriminator — the priority order Account-Page +
 * Start-Page render against. `null` = no lifecycle event active; the
 * caller falls through to credit-derived UI via `useEntitlement()`.
 *
 * Ordering matters: `past_due` wins over `pending_cancel` (Stripe sets
 * `status='past_due'` independently of the cancel flag, and Past-Due
 * is the most action-required state). `pending_cancel` wins over
 * `pending_plan_change` (a user with both flags is being cancelled
 * outright; the plan-change becomes moot).
 *
 * Backend DB field names (`pendingDowngrade*`) are persisted; user-
 * facing strings rendered from these on the PWA side use "Plan-Wechsel"
 * semantics per Batch 9A FB-7.
 */
export type LifecyclePhase = 'past_due' | 'pending_cancel' | 'pending_plan_change' | null;

export function inferLifecyclePhase(state: SubscriptionState | null): LifecyclePhase {
  if (!state) return null;
  if (state.isPastDue) return 'past_due';
  if (state.cancelAtPeriodEnd) return 'pending_cancel';
  if (state.pendingDowngradeTier) return 'pending_plan_change';
  return null;
}

// ── Module-level cache (mirrors useEntitlement.ts pattern) ────

interface UseSubscriptionStateResult {
  state: SubscriptionState | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

let cache: SubscriptionState | null = null;
let inflight: Promise<SubscriptionState> | null = null;
// cacheGeneration (Bug 2, Flag 2): epoch bumped on every clear. A fetch captures
// it at start and only writes cache / clears inflight if it still matches, so a
// fetch in flight when a clear happens cannot re-populate the cache afterwards.
let cacheGeneration = 0;

/**
 * Cache invalidation. Wired into the story-create success path (B1),
 * asset-terminal settle (B2), quota-surface mounts (B3), and the logout flow.
 * Symmetric with `clearEntitlementCache()`.
 */
export function clearSubscriptionStateCache(): void {
  cache = null;
  inflight = null;
  cacheGeneration += 1;
}

/**
 * Cross #9 Sub-Issue 6 (2026-05-04) + post-checkout-cascade fix
 * (2026-05-18) — detect the cascade-race partial-state fingerprints
 * the backend may return during the Stripe-webhook cascade window.
 *
 * The backend already retries up to 1.5s (see backend `findActive
 * SubscriptionWithRetry`). This PWA-side check is layer-2 insurance
 * for the case where the backend retry budget exhausts and a partial
 * response still reaches the client. Without bypass, the partial
 * response gets module-cached for the entire session.
 *
 * Two distinct partial-state fingerprints are caught:
 *
 *   1. Past_due-stub: the canonical stub `handleSubscriptionCheckoutCompleted`
 *      writes — `status='past_due'` AND `currentPeriodStart ===
 *      currentPeriodEnd` (both set to `event.created`). Hit when the
 *      backend retry returns the stub itself before Webhook 2 lands.
 *      Symptom (smoke-test 21:30, 2026-05-04): cached past_due +
 *      placeholder period dates rendering as "no plan name + pack-
 *      style count".
 *
 *   2. Trial-state-branch: the backend's `getStateForUser` `if (!sub)`
 *      branch fires when `findActiveSubscriptionWithRetry` returns
 *      null after exhausting its 1.5s retry budget. The response has
 *      `status='trial'` AND every subscription-identifier field null.
 *      Indistinguishable on the wire from a genuine trial user (by
 *      design — the backend gives the PWA a uniform shape for both
 *      "no sub yet" and "really trial"). Hit when the Stripe webhook
 *      cascade lags >1.5s. Symptom (live smoke test, 2026-05-18):
 *      cached trial-state → plan name renders as "Abo" on /account
 *      and Start, subscription card missing from /account/subscription.
 *
 * Real past_due payment-failures have unequal period dates → don't
 * match fingerprint #1. The trial-state-branch fingerprint requires
 * ALL of `subscriptionId`, `stripeSubscriptionId`, and `currentPeriodStart`
 * to be null — this discriminates against any future canonical
 * trialing shape (a real Stripe-trial-period sub carries a non-null
 * `subscriptionId` from the canonical writer).
 *
 * String-compare on currentPeriodStart === currentPeriodEnd works
 * because the backend serializes both as ISO-8601 strings.
 */
/**
 * Trial-state-branch fingerprint helper (post-checkout cascade fix
 * 2026-05-18). Matches the exact shape `getStateForUser` returns when
 * `findActiveSubscriptionWithRetry` exhausts its retry budget
 * (subscriptions.service.ts:555-578). Used by:
 *   - `isPartialCascadeState` clause #2 (retry-trigger),
 *   - the retry-cache-decision gate in `handleResult` below
 *     (only force-cache the retry result for THIS fingerprint, so the
 *     pathological past_due-stub-on-both-fetches case retains its
 *     never-cache behavior and allows subsequent mounts to recover).
 */
function isTrialStateBranchShape(state: SubscriptionState): boolean {
  return (
    state.status === 'trial' &&
    state.subscriptionId === null &&
    state.stripeSubscriptionId === null &&
    state.currentPeriodStart === null
  );
}

export function isPartialCascadeState(state: SubscriptionState): boolean {
  // Fingerprint 1 — past_due-stub (Cross #9 Sub-Issue 6).
  if (
    state.status === 'past_due' &&
    state.currentPeriodStart !== null &&
    state.currentPeriodEnd !== null &&
    state.currentPeriodStart === state.currentPeriodEnd
  ) {
    return true;
  }
  // Fingerprint 2 — trial-state-branch (post-checkout cascade fix
  // 2026-05-18). Body is factored into `isTrialStateBranchShape` so
  // the retry-cache-decision gate can reuse the exact same predicate.
  return isTrialStateBranchShape(state);
}

const PARTIAL_STATE_REFETCH_MS = 2500;

async function fetchSubscriptionState(forceCacheRegardless = false): Promise<SubscriptionState> {
  if (cache) return cache;
  if (inflight) return inflight;

  const gen = cacheGeneration;
  inflight = apiFetch<SubscriptionState>('/api/subscriptions/me')
    .then((result) => {
      // Cross #9 Sub-Issue 6 — by default, do NOT cache partial-state
      // responses; the hook's useEffect schedules a single refetch.
      // `forceCacheRegardless=true` is passed by the retry path
      // (handleResult below) ONLY when the FIRST response matched the
      // trial-state-branch fingerprint. Force-caching on retry under
      // that condition prevents genuine trial users from re-fetching
      // on every route mount (their response stays trial-state-branch).
      // Past_due-stub first responses do NOT force-cache the retry, so
      // the rare pathological >4s-cascade case retains the pre-fix
      // "subsequent mounts re-fetch and recover" behavior. Bounded
      // recursion is unchanged: `!isRetry` in handleResult prevents
      // scheduling a further retry.
      // Gen guard (Flag 2): drop the write if a clear happened mid-flight.
      if (gen === cacheGeneration && (forceCacheRegardless || !isPartialCascadeState(result))) {
        cache = result;
      }
      return result;
    })
    .finally(() => {
      if (gen === cacheGeneration) inflight = null;
    });

  return inflight;
}

/**
 * Clear + warm the subscription-state cache (Bug 2, B2). Best-effort module-level
 * refresh used after a story's assets settle, so the next quota-surface mount
 * reads the correct plan + remaining.
 */
export async function refetchSubscriptionStateCache(): Promise<void> {
  clearSubscriptionStateCache();
  await fetchSubscriptionState();
}

/**
 * React hook. Module-level cache + inflight-promise dedupe so multiple
 * mounts in the same session share a single fetch. `refetch()` clears
 * the cache and re-fires; intended for use after backend mutations
 * (e.g. post-`reactivate` PWA call → wait ~500ms for webhook sync →
 * `refetch()`).
 *
 * Error model: 401 from `apiFetch` is handled by the api-client's
 * refresh flow. Other errors surface as `error: <message>` and
 * `state: null`. The hook does NOT throw — consumers render gracefully
 * (Account-Page falls back to a default rendering when state is null).
 */
export function useSubscriptionState(options?: {
  refetchOnMount?: boolean;
}): UseSubscriptionStateResult {
  const refetchOnMount = options?.refetchOnMount ?? false;
  const [state, setState] = useState<SubscriptionState | null>(cache);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!cache);

  useEffect(() => {
    // B3 (Bug 2): force a fresh fetch on mount for the abo page + plan label +
    // quota surfaces so they never render a stale module cache (e.g. the
    // post-upgrade "Entdecker" label, or "Noch 5 von 5" that didn't move).
    if (refetchOnMount) clearSubscriptionStateCache();
    let cancelled = false;
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;

    const handleError = (err: unknown): void => {
      if (cancelled) return;
      if (err instanceof ApiError) {
        setError(err.envelope?.user_message ?? `HTTP ${err.httpStatus}`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Subscription state konnte nicht geladen werden.');
      }
      setIsLoading(false);
    };

    const handleResult = (result: SubscriptionState, isRetry: boolean): void => {
      if (cancelled) return;
      setState(result);
      setError(null);
      setIsLoading(false);

      // Cross #9 Sub-Issue 6 — partial-state cache-bypass + refetch.
      // Only the FIRST fetch in this hook's lifecycle schedules a
      // retry; the retry result (whatever it is) is final, even if
      // still partial. Bounded recursion prevents infinite loops if
      // the backend returns partial responses in succession.
      if (!isRetry && isPartialCascadeState(result)) {
        // Gate forceCacheRegardless on the FIRST response's shape, not
        // unconditionally true. For trial-state-branch first responses,
        // force-caching after retry is required (genuine trial users
        // would otherwise re-fetch on every mount; post-checkout users
        // whose retry returns canonical also benefit). For past_due-stub
        // first responses, force-caching the retry would block the
        // pre-fix "subsequent mounts re-fetch and recover" recovery on
        // the rare pathological >4s-cascade case — so we keep
        // forceCacheRegardless=false for that branch, restoring the
        // pre-fix never-cache behavior for past_due-stub-on-both-fetches.
        const forceCacheOnRetry = isTrialStateBranchShape(result);
        refetchTimer = setTimeout(() => {
          if (cancelled) return;
          void fetchSubscriptionState(forceCacheOnRetry)
            .then((r) => handleResult(r, true))
            .catch(handleError);
        }, PARTIAL_STATE_REFETCH_MS);
      }
    };

    void fetchSubscriptionState()
      .then((r) => handleResult(r, false))
      .catch(handleError);

    return () => {
      cancelled = true;
      if (refetchTimer !== null) clearTimeout(refetchTimer);
    };
  }, [refetchOnMount]);

  const refetch = async (): Promise<void> => {
    clearSubscriptionStateCache();
    setIsLoading(true);
    try {
      const result = await fetchSubscriptionState();
      setState(result);
      setError(null);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(err.envelope?.user_message ?? `HTTP ${err.httpStatus}`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Subscription state konnte nicht geladen werden.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { state, isLoading, error, refetch };
}
